// matchPath is used by toSwitchedProject / toSwitchedProjectView
import type { URLSearchParamsInit } from 'react-router';
// oxlint-disable-next-line no-restricted-imports
import { createSearchParams, matchPath } from 'react-router';
import type { AppRoute } from '@gonfalon/router/generated/routeInfo';
import { appRoute, unauthenticatedRoute } from '@gonfalon/router/generated/routeInfo';
import nullthrows from 'nullthrows';

import { buildPath, withHash, withSearchParams } from './internal';
import { mergePlaygroundCompareIntoSearchParams } from './internal/playgroundCompareSearchParams';
import { withChangeHistory } from './internal/withChangeHistory';
import { withNewProjectContext } from './internal/withNewProjectContext';
import { withoutProjectScopedSearchParams } from './internal/withoutProjectScopedSearchParams';
import { withProjectContext } from './internal/withProjectContext';

export type { Path } from 'react-router';

export { toHref } from './internal/toHref';

type URLOptions = {
  search?: URLSearchParamsInit;
  hash?: string;
};

type WithChangeHistory<T extends object> = T & { changeHistory?: true | string };

export function buildEnvironmentSearchParams(
  selectedEnvironmentKey: string | null | undefined,
  environments: string[] = [],
  others?: URLSearchParamsInit,
) {
  const searchParams = createSearchParams(others);

  for (const env of environments) {
    if (!searchParams.has('env', env)) {
      searchParams.append('env', env);
    }
  }

  if (typeof selectedEnvironmentKey === 'string') {
    searchParams.set('selected-env', selectedEnvironmentKey);
  }

  return searchParams;
}

export function toRoot({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/'), withSearchParams(search), withHash(hash));
}

export function toProjectHome({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/projects/:projectKey', { projectKey }), withSearchParams(search), withHash(hash));
}

export function toHome({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/home', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toFlags({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/flags', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toCreateSegment(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/new', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toSegments(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

/**
 * @deprecated
 */
export function toLegacyExperiments(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/experiments/legacy',
      { projKey: projectKey, envKey: environmentKey },
    ),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
  );
}

export function toMetricGroup(
  {
    projectKey,
    metricGroupKey,
  }: {
    projectKey: string;
    metricGroupKey: string;
  },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/groups/:metricGroupKey/details', { projectKey, metricGroupKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMetricDetails(
  { projectKey, metricKey }: { projectKey: string; metricKey: string },
  { search, hash, changeHistory }: WithChangeHistory<URLOptions> = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/:metricKey/details', { projectKey, metricKey }),
    withSearchParams(search),
    withHash(hash),
    withChangeHistory(changeHistory),
  );
}

export function toMetric(
  { projectKey, metricKey, environmentKey }: { projectKey: string; metricKey: string; environmentKey?: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/:metricKey/details', {
      projectKey,
      metricKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMetrics({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toUxInsights({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ux-insights', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toObservabilityMetrics({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/observability-metrics', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toObserve(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/observe', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toInternalUrl(
  path: string,
  { search, hash, environmentKey }: URLOptions & { environmentKey?: string } = {},
) {
  return buildPath(
    path,
    withSearchParams(buildEnvironmentSearchParams(environmentKey ?? null, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function getProductAnalyticsAppRoute({ projectKey }: { projectKey: string }) {
  return appRoute('/projects/:projectKey/product-analytics', { projectKey });
}

export function toProductAnalyticsRoute(
  relativePath: string,
  { projectKey, environmentKey, ...params }: { projectKey: string; environmentKey: string } & Record<string, string>,
) {
  let path = getProductAnalyticsAppRoute({ projectKey });
  path += relativePath;
  return buildPath(
    path,
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], params)),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toProductAnalytics({ projectKey, environmentKey }: { projectKey: string; environmentKey: string }) {
  return buildPath(
    getProductAnalyticsAppRoute({ projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [])),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}
export function toSnowflakeProductAnalyticsIntegration() {
  return '/product-analytics/integrations/snowflake';
}

export function toObserveNewMetric(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/observe/new-metric', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMetricsEvents(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/events', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMetricsEventsExpand(
  {
    projectKey,
    environmentKey,
    eventKey,
    dataSourceKey,
    eventKind,
  }: { projectKey: string; environmentKey: string; eventKey: string; dataSourceKey: string; eventKind: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/events/expand/:dataSourceKey/:eventKind/:eventKey', {
      projectKey,
      eventKey,
      dataSourceKey,
      eventKind,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toLegacyObserveLocation<T extends { pathname: string }>(location: T): T {
  return {
    ...location,
    pathname: location.pathname.replace(/\/metrics\/events(?=\/|$)/, '/observe'),
  };
}

export function toMetricsEventsLocation<T extends { pathname: string }>(location: T): T {
  return {
    ...location,
    pathname: location.pathname.replace(/^(\/projects\/[^/]+)\/observe(?=\/|$)/, '$1/metrics/events'),
  };
}

export function toObserveExpand(
  {
    projectKey,
    environmentKey,
    eventKey,
    dataSourceKey,
    eventKind,
  }: { projectKey: string; environmentKey: string; eventKey: string; dataSourceKey: string; eventKind: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/observe/expand/:dataSourceKey/:eventKind/:eventKey', {
      projectKey,
      eventKey,
      dataSourceKey,
      eventKind,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperiments(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBandits(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toCreateMultiArmedBandit(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/new', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBanditDesign(
  {
    projectKey,
    multiArmedBanditKey,
    environmentKey,
  }: { projectKey: string; multiArmedBanditKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/design', {
      projectKey,
      multiArmedBanditKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBanditResults(
  {
    projectKey,
    multiArmedBanditKey,
    environmentKey,
  }: { projectKey: string; multiArmedBanditKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/results', {
      projectKey,
      multiArmedBanditKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBanditIterations(
  {
    projectKey,
    environmentKey,
    multiArmedBanditKey,
  }: { projectKey: string; environmentKey: string; multiArmedBanditKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/iterations', {
      projectKey,
      multiArmedBanditKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBanditExposures(
  {
    projectKey,
    environmentKey,
    multiArmedBanditKey,
  }: { projectKey: string; environmentKey: string; multiArmedBanditKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/exposures', {
      projectKey,
      multiArmedBanditKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMultiArmedBanditIteration(
  {
    projectKey,
    multiArmedBanditKey,
    iterationId,
  }: { projectKey: string; multiArmedBanditKey: string; iterationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/multi-armed-bandits/:multiArmedBanditKey/iterations/:iterationId', {
      projectKey,
      multiArmedBanditKey,
      iterationId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toHoldoutsRoot({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/holdouts', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toCreateHoldout({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/holdouts/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toHoldoutDetails(
  { projectKey, holdoutKey, environmentKey }: { projectKey: string; holdoutKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/holdouts/:holdoutKey/details', { projectKey, holdoutKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toHoldoutDesign(
  { projectKey, holdoutKey, environmentKey }: { projectKey: string; holdoutKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/holdouts/:holdoutKey/design', { projectKey, holdoutKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toLiveEvents(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/live', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toLiveFlagEvents(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/live/flags/:flagKey', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

/**
 * @deprecated
 */
export function toAuditLog(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/audit/*',
      { projKey: projectKey, envKey: environmentKey },
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlag(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey?: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagTargeting(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash, changeHistory }: WithChangeHistory<URLOptions> = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
    withChangeHistory(changeHistory),
  );
}

export function toSegmentTargeting(
  { projectKey, segmentKey, environmentKey }: { projectKey: string; segmentKey: string; environmentKey: string },
  { search, hash, changeHistory }: WithChangeHistory<URLOptions> = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/:segmentKey/targeting', {
      projectKey,
      segmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
    withChangeHistory(changeHistory),
  );
}

export function toSegmentTargetingPendingChanges(
  { projectKey, segmentKey, environmentKey }: { projectKey: string; segmentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/:segmentKey/targeting/pending-changes', {
      projectKey,
      segmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toSegmentFlags(
  { projectKey, segmentKey, environmentKey }: { projectKey: string; segmentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/:segmentKey/overview', {
      projectKey,
      segmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toSegmentSettings(
  { projectKey, segmentKey, environmentKey }: { projectKey: string; segmentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/:segmentKey/settings', {
      projectKey,
      segmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toSegmentHistory(
  { projectKey, segmentKey, environmentKey }: { projectKey: string; segmentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments/:segmentKey/history/*', {
      projectKey,
      segmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagVariations(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/variations', {
      projectKey,
      flagKey,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

/**
 * @deprecated
 */
export function toFlagHistory(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey?: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/features/:flagKey/history/*',
      {
        projKey: projectKey,
        envKey: nullthrows(environmentKey),
        flagKey,
      },
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagVariationPendingChanges(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/variations/pending-changes', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagVariationConflict(
  {
    projectKey,
    flagKey,
    conflictIndex,
    environmentKey,
  }: { projectKey: string; flagKey: string; conflictIndex: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/variations/conflicts/:conflictIndex', {
      projectKey,
      flagKey,
      conflictIndex,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagTargetingConflict(
  {
    projectKey,
    flagKey,
    conflictIndex,
    environmentKey,
  }: { projectKey: string; flagKey: string; conflictIndex: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/conflicts/:conflictIndex', {
      projectKey,
      flagKey,
      conflictIndex,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagEnvironmentSettings(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/environments', {
      projectKey,
      flagKey,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toFlagDefaults({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/flags', {
      projectKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagSettings(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/settings', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagPendingChange(
  {
    projectKey,
    flagKey,
    pendingId,
    environmentKey,
  }: { projectKey: string; flagKey: string; pendingId: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/pending-changes/:pendingId', {
      projectKey,
      flagKey,
      pendingId,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagPendingTargetingChanges(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/pending-changes', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagExpiringTargets(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/expiring-targets', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagExpiringTarget(
  {
    projectKey,
    flagKey,
    expiringId,
    environmentKey,
  }: { projectKey: string; flagKey: string; expiringId: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/expiring-targets/:expiringId', {
      projectKey,
      flagKey,
      expiringId,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toApprovals({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/approvals', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toApproval(
  { approvalRequestId, projectKey }: { projectKey: string; approvalRequestId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/approvals/:approvalRequestId', {
      approvalRequestId,
      projectKey: nullthrows(projectKey),
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagApproval(
  {
    projectKey,
    flagKey,
    approvalRequestId,
    environmentKey,
  }: { projectKey: string; flagKey: string; approvalRequestId: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/approvals/:approvalRequestId', {
      projectKey,
      flagKey,
      approvalRequestId,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toContexts(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/contexts', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toContextKinds(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/context-kinds', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

/**
 * @deprecated
 */
export function toConnectFlag(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/features/connect',
      { projKey: projectKey, envKey: environmentKey },
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateFlag(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/new', {
      projectKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toArchiveFlagFromList(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/features/:flagKey/archive',
      {
        projKey: projectKey,
        envKey: environmentKey,
        flagKey,
      },
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDeprecateFlagFromList(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/settings/deprecate', { projectKey, flagKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAddToCode(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/add-to-code', { projectKey, flagKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

// The Code tab on the flag page. Carries the environment the way the other flag tabs do, since the
// add-to-code content shows the flag's live On/Off state per environment.
export function toFlagCode(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/code', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toArchiveFlagFromSettings(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/settings/archive', { projectKey, flagKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}
export function toDeprecateFlagFromSettings(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/settings/deprecate', { projectKey, flagKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagEnvironmentOverview(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/environment-overview', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagExperiments({
  projectKey,
  flagKey,
  environmentKey,
}: {
  projectKey: string;
  flagKey: string;
  environmentKey: string;
}) {
  // flags no longer have an experiment tab, experiments are listed in the flag sidebar
  return toFlagTargeting({ projectKey, flagKey, environmentKey });
}

/**
 * @deprecated
 */
export function toFlagExperimentsSettings(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/:projKey/:envKey/features/:flagKey/experiments/settings',
      {
        projKey: projectKey,
        envKey: environmentKey,
        flagKey,
      },
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagComparison(
  {
    projectKey,
    environmentPair,
    environmentKey,
  }: { projectKey: string; environmentPair: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/compare/:environmentPair', { projectKey, environmentPair }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagSync(
  {
    projectKey,
    flagKey,
    environmentPair,
    environmentKey,
  }: { projectKey: string; flagKey: string; environmentPair: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/compare/:environmentPair/flags/:flagKey', {
      projectKey,
      flagKey,
      environmentPair,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toContextInstance(
  { projectKey, instanceId, environmentKey }: { projectKey: string; instanceId: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/contexts/instances/:instanceId', { projectKey, instanceId }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toContext(
  { projectKey, kind, key, environmentKey }: { projectKey: string; kind: string; key: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/contexts/kinds/:kind/instances/:key', { projectKey, kind, key }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagWorkflows(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/workflows', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagMonitoring(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/monitoring', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagOverview(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/overview', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagAudience(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/audience', {
      projectKey,
      flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagUserFeedback(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/feedback', {
      projectKey,
      flagKey,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagCodeReferences(
  { projectKey, flagKey }: { projectKey: string; flagKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/code-refs', { projectKey, flagKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toFlagLinks(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/links', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagWorkflow(
  {
    projectKey,
    flagKey,
    workflowId,
    environmentKey,
  }: { projectKey: string; flagKey: string; workflowId: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/workflows/:workflowId', { projectKey, flagKey, workflowId }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toCreateFlagWorkflow(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/workflows/new', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toFlagExcludeRules(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting/exclude-rules', { projectKey, flagKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentDesign(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/design', { projectKey, experimentKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentResults(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/results', { projectKey, experimentKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentTraffic(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/traffic', { projectKey, experimentKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentIterations(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/iterations', {
      projectKey,
      experimentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentAnalytics(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/analytics', {
      projectKey,
      experimentKey,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentExposures(
  { projectKey, experimentKey, environmentKey }: { projectKey: string; experimentKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/exposures', {
      projectKey,
      experimentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

function toExperimentTabPendingChanges(
  tab: 'design' | 'results',
  {
    projectKey,
    experimentKey,
    environmentKey,
    flagKey,
  }: { projectKey: string; experimentKey: string; environmentKey: string; flagKey?: string },
  { search, hash }: URLOptions = {},
) {
  const searchParams = buildEnvironmentSearchParams(environmentKey, [], search);
  if (flagKey) {
    searchParams.set('flagKey', flagKey);
  }
  const path =
    tab === 'design'
      ? appRoute('/projects/:projectKey/experiments/:experimentKey/design/pending-changes', {
          projectKey,
          experimentKey,
        })
      : appRoute('/projects/:projectKey/experiments/:experimentKey/results/pending-changes', {
          projectKey,
          experimentKey,
        });
  return buildPath(
    path,
    withSearchParams(searchParams),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toExperimentDesignPendingChanges(
  params: { projectKey: string; experimentKey: string; environmentKey: string; flagKey?: string },
  options: URLOptions = {},
) {
  return toExperimentTabPendingChanges('design', params, options);
}

export function toExperimentResultsPendingChanges(
  params: { projectKey: string; experimentKey: string; environmentKey: string; flagKey?: string },
  options: URLOptions = {},
) {
  return toExperimentTabPendingChanges('results', params, options);
}

export function toCreateExperiment(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/new', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toCreateMetric(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/new', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toCreateMetricGroup(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/metrics/groups/new', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toDataSources({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/metrics', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesAssignments({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/assignments', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesAttributes({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/attributes', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateDataSourcesMetric({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/metrics/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateDataSourcesAssignment(
  { projectKey }: { projectKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/assignments/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateDataSourcesAttribute(
  { projectKey }: { projectKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/attributes/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesMetricConnections(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/metrics/:dataSourceKey/connections', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesMetricConnectionsCreateMetric(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/metrics/:dataSourceKey/connections/new-metric', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesMetricConfiguration(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/metrics/:dataSourceKey/configuration', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesMetricDetails(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  options: URLOptions = {},
) {
  return toDataSourcesMetricConnections({ projectKey, dataSourceKey }, options);
}

export function toDataSourcesAssignmentConnections(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/assignments/:dataSourceKey/connections', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesAssignmentConfiguration(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/assignments/:dataSourceKey/configuration', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesAssignmentDetails(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  options: URLOptions = {},
) {
  return toDataSourcesAssignmentConnections({ projectKey, dataSourceKey }, options);
}

export function toDataSourcesAttributeConnections(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/attributes/:dataSourceKey/connections', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDataSourcesAttributeConfiguration(
  { projectKey, dataSourceKey }: { projectKey: string; dataSourceKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/data-sources/attributes/:dataSourceKey/configuration', {
      projectKey,
      dataSourceKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toExperimentIteration(
  { projectKey, experimentKey, iterationId }: { projectKey: string; experimentKey: string; iterationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/experiments/:experimentKey/iterations/:iterationId', {
      projectKey,
      experimentKey,
      iterationId,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toTeamHistory({ teamKey }: { teamKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/teams/:teamKey/history/*', { teamKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export type ReleaseSettingsTab = 'environment-release-plans' | 'release-policies' | 'release-automation';

export function toReleaseSettings(
  { projectKey, tab }: { projectKey: string; tab?: ReleaseSettingsTab },
  { search, hash }: URLOptions = {},
) {
  const searchParams = createSearchParams(search);
  if (tab) {
    searchParams.set('tab', tab);
  }

  return buildPath(
    appRoute('/projects/:projectKey/settings/releases', { projectKey }),
    withSearchParams(searchParams),
    withHash(hash),
  );
}

export function toCreateReleasePolicy({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/release-policies/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toReleasePolicy(
  { projectKey, releasePolicyKey }: { projectKey: string; releasePolicyKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/release-policies/:releasePolicyKey', {
      projectKey,
      releasePolicyKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateEnvironmentReleasePlan(
  { projectKey }: { projectKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/environment-release-plans/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toEditEnvironmentReleasePlan(
  { projectKey, releasePlanKey }: { projectKey: string; releasePlanKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/environment-release-plans/:releasePlanKey/edit', {
      projectKey,
      releasePlanKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toReleasePipelines({ projectKey }: { projectKey: string }, options: URLOptions = {}) {
  return toReleaseSettings({ projectKey, tab: 'release-automation' }, options);
}

export function toReleasePipeline(
  { projectKey, releasePipelineKey }: { projectKey: string; releasePipelineKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/release-pipelines/:releasePipelineKey', {
      projectKey,
      releasePipelineKey,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toCreateReleasePipeline({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/releases/release-pipelines/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toReleases(
  { projectKey, releasePipelineKey }: { projectKey: string; releasePipelineKey?: string },
  { search, hash }: URLOptions = {},
) {
  if (!releasePipelineKey) {
    // this only happens if you try to navigate to the releases view when no release pipelines exist yet
    return toReleasePipelines({ projectKey }, { search, hash });
  }

  return buildPath(
    appRoute('/projects/:projectKey/release-pipelines/:releasePipelineKey', { projectKey, releasePipelineKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toReleasesList({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/release-pipelines', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAccountSettings({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/general'), withSearchParams(search), withHash(hash));
}

export function toUnifiedSettings({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/projects'), withSearchParams(search), withHash(hash));
}

export const PROJECT_SETTINGS_DRAWER_PARAM = 'projectSettings';

export function toProjectSettingsDrawer({ projectKey, search }: { projectKey: string; search?: URLSearchParams }) {
  const next = new URLSearchParams(search);
  next.set(PROJECT_SETTINGS_DRAWER_PARAM, projectKey);
  return toProjects({ search: next });
}

export function toIntegrations({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/integrations'), withSearchParams(search), withHash(hash));
}

export function toIntegration({ kind }: { kind: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/integrations/:kind/new', { kind }), withSearchParams(search), withHash(hash));
}

export function toIntegrationEdit(
  { kind, subscriptionId }: { kind: string; subscriptionId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/settings/integrations/:kind/:subscriptionId/edit', { kind, subscriptionId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toJoinOrganization({ token }: { token?: string }, { search }: URLOptions = {}) {
  if (token) {
    return buildPath(unauthenticatedRoute('/join-your-organization/:token', { token }), withSearchParams(search));
  }
  return buildPath(unauthenticatedRoute('/join-your-organization'), withSearchParams(search));
}

export function toJoinOrganizationSso({ token, accountId }: { token?: string; accountId: string }) {
  if (token) {
    return buildPath(`/join-your-organization/${encodeURIComponent(token)}/sso/${encodeURIComponent(accountId)}`);
  }
  return buildPath(`/join-your-organization/sso/${encodeURIComponent(accountId)}`);
}

export function toWelcome({ token }: { token?: string }, { search, hash }: URLOptions = {}) {
  if (token) {
    return buildPath(unauthenticatedRoute('/welcome/:token', { token }), withSearchParams(search), withHash(hash));
  }
  return buildPath(unauthenticatedRoute('/welcome'), withSearchParams(search), withHash(hash));
}

export function toPendingInvite({ token }: { token?: string }, { search }: URLOptions = {}) {
  if (token) {
    return buildPath(unauthenticatedRoute('/pending-invite/:token', { token }), withSearchParams(search));
  }
  return buildPath(unauthenticatedRoute('/pending-invite'), withSearchParams(search));
}

export function toChooseOrganization({ email }: { email: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    unauthenticatedRoute('/choose-organization/:email', { email }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toSdkSetup({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/sdk-setup', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toChooseOrganizationCheckYourEmail() {
  return buildPath(unauthenticatedRoute('/choose-organization/check-your-email'));
}

export function toVerifyEmail({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/verify-email'), withSearchParams(search), withHash(hash));
}

export function toWelcomeSurvey({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/welcome-survey'), withSearchParams(search), withHash(hash));
}

export function toCheckYourEmail({ search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/check-your-email'), withSearchParams(search), withHash(hash));
}

export function toVerifyOrganizationPassword(
  { email, selectedOrganization }: { email: string; selectedOrganization: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    unauthenticatedRoute('/choose-organization/:email/:selectedOrganization/enter-password', {
      email,
      selectedOrganization,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toSignup({ search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/signup'), withSearchParams(search), withHash(hash));
}

export function toAgentControlSignup({ search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/signup-agentcontrol'), withSearchParams(search), withHash(hash));
}

export function toFacilitatedTrialSignup({ token }: { token: string }, { search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/signup/:token', { token }), withSearchParams(search), withHash(hash));
}

export function toJoin({ search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/join'), withSearchParams(search), withHash(hash));
}

export function toLogin({ search, hash }: URLOptions = {}) {
  return buildPath(unauthenticatedRoute('/login'), withSearchParams(search), withHash(hash));
}

export function toBilling({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/billing'), withSearchParams(search), withHash(hash));
}

export function toBillingPlans({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/billing/plans'), withSearchParams(search), withHash(hash));
}

export function toReviewBillingPlan({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/billing/plans/review'), withSearchParams(search), withHash(hash));
}

export function toCustomizeBillingPlan({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/billing/plans/customize'), withSearchParams(search), withHash(hash));
}

export function toBillingPlanConfirmation({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/billing/plans/confirmation'), withSearchParams(search), withHash(hash));
}

export function toProjects({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/projects'), withSearchParams(search), withHash(hash));
}

export function toGeneralSettings({ search, hash, changeHistory }: WithChangeHistory<URLOptions> = {}) {
  return buildPath(
    appRoute('/settings/general'),
    withSearchParams(search),
    withHash(hash),
    withChangeHistory(changeHistory),
  );
}

export function toProjectSettings(
  { projectKey }: { projectKey: string },
  { search, hash, changeHistory }: WithChangeHistory<URLOptions> = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withChangeHistory(changeHistory),
  );
}

export function toProjectEnvironments({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/environments', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toPayloadFiltersSettings({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/payload-filters', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateEnvironment({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/environments/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toEditProjectEnvironment(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/environments/:envKey/edit', { projectKey, envKey: environmentKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toProjectEnvironmentApprovalSettings(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/environments/:envKey/approval-settings', {
      projectKey,
      envKey: environmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toProjectEnvironmentKeys(
  { projectKey, environmentKey }: { projectKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/environments/:envKey/keys', {
      projectKey,
      envKey: environmentKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toMembers({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/members'), withSearchParams(search), withHash(hash));
}

/**
 * @deprecated
 */
export function toMembersInvite({ search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute(
      // @ts-expect-error TODO: remove this old-IA navigator
      '/settings/members/invite',
    ),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMemberAccessSummary({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/members/:memberId/access-summary', { memberId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMemberPermissions({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/members/:memberId/permissions', { memberId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMemberSettings({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/members/:memberId/settings', { memberId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMember({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/members/:memberId', { memberId }), withSearchParams(search), withHash(hash));
}

export function toMemberHistory({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/members/:memberId/history/*', { memberId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toMemberTeams({ memberId }: { memberId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/members/:memberId/teams', { memberId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toProfile({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/profile'), withSearchParams(search), withHash(hash));
}

export function toApplications({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/applications'), withSearchParams(search), withHash(hash));
}

export function toApplication({ applicationKey }: { applicationKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey', { applicationKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toApplicationVersions(
  { applicationKey }: { applicationKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey/versions', { applicationKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toApplicationVersionSupportStatus(
  { applicationKey, versionKey }: { applicationKey: string; versionKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey/versions/:versionKey/support-status', {
      applicationKey,
      versionKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toEditApplication({ applicationKey }: { applicationKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey/edit', { applicationKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toApplicationHistory(
  { applicationKey }: { applicationKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey/history/*', { applicationKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateApplicationVersion(
  { applicationKey }: { applicationKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/settings/applications/:applicationKey/versions/new', { applicationKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateApplication({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/applications/new'), withSearchParams(search), withHash(hash));
}

export function toServerUsageDetails({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toClientUsageDetails({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toExperimentUsageDetails({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toDataExportUsageDetails({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toHostUsageDetails({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toUsageOverview({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toUsageDetailsOverview({ search, hash }: URLOptions = {}) {
  return toPlanUsage({ search, hash });
}

export function toPlanUsage({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/plan-usage'), withSearchParams(search), withHash(hash));
}

export function toPlanUsageDetails({ metric }: { metric: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/plan-usage/details/:metric', { metric }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDiagnosticUsage({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/diagnostic-usage'), withSearchParams(search), withHash(hash));
}

export function toDiagnosticUsageDetails({ metric }: { metric: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/diagnostic-usage/details/:metric', { metric }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toSdks({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/sdks'), withSearchParams(search), withHash(hash));
}

export function toSdksDetails({ applicationId }: { applicationId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/sdks/:applicationId', { applicationId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toTokenLimits({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/token-limits'), withSearchParams(search), withHash(hash));
}

export function toReleaseAutopilotSettings({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/release-autopilot'), withSearchParams(search), withHash(hash));
}

export function toViewCustomRole({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/:key/view', { key: roleKey }), withSearchParams(search), withHash(hash));
}

export function toEditCustomRole({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/:key/edit', { key: roleKey }), withSearchParams(search), withHash(hash));
}

export function toSecurity({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/security'), withSearchParams(search), withHash(hash));
}

export function toSdkKeys({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/sdk-keys'), withSearchParams(search), withHash(hash));
}

export function toAuthorization({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/authorization'), withSearchParams(search), withHash(hash));
}

export function toEditPayloadFilter(
  { projectKey, payloadFilterKey }: { projectKey: string; payloadFilterKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/payload-filters/:filterKey/edit', {
      projectKey,
      filterKey: payloadFilterKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toRelayProxies({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/relay'), withSearchParams(search), withHash(hash));
}

export function toCreateRelayProxy({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/relay/new'), withSearchParams(search), withHash(hash));
}

export function toEditRelayProxy({ relayProxyId }: { relayProxyId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/relay/:id/edit', { id: relayProxyId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCustomRoles({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles'), withSearchParams(search), withHash(hash));
}

export function toViewRole({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/:key/view', { key: roleKey }), withSearchParams(search), withHash(hash));
}

export function toCreateCustomRole({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/new'), withSearchParams(search), withHash(hash));
}

export function toTeams({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/teams'), withSearchParams(search), withHash(hash));
}

export function toTeamMembers({ teamKey }: { teamKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/teams/:teamKey/members', { teamKey }), withSearchParams(search), withHash(hash));
}

export function toTeamAccessSummary({ teamKey }: { teamKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/teams/:teamKey/access-summary', { teamKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toTeamPermissions({ teamKey }: { teamKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/teams/:teamKey/permissions', { teamKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toTeamSettings({ teamKey }: { teamKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/teams/:teamKey/settings', { teamKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAccountHistory({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/history/*', {}), withSearchParams(search), withHash(hash));
}

export function toAuthorizationCustomRoles({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/authorization/roles/:key', { key: roleKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateAccessToken({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/authorization/tokens/new'), withSearchParams(search), withHash(hash));
}

export function toEditAccessToken({ tokenId }: { tokenId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/authorization/tokens/:tokenId/edit', { tokenId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toResetAccessToken({ tokenId }: { tokenId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/authorization/tokens/:tokenId/reset', { tokenId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCloneAccessToken({ tokenId }: { tokenId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/authorization/tokens/:tokenId/clone', { tokenId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toSAMLConfig({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/security/saml/config'), withSearchParams(search), withHash(hash));
}

export function toReviewOAuthApp({ appId }: { appId: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/authorization/oauth/:appId/review', { appId }),
    withSearchParams(search),
    withHash(hash),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toFlagTargetingShortcut(
  {
    projectKey,
    flagKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; flagKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags/:flagKey/targeting', {
      projectKey,
      flagKey,
    }),
    withHash(hash),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toFlagsShortcut(
  {
    projectKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/flags', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withHash(hash),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toContextsShortcut(
  {
    projectKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/contexts', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withHash(hash),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toSegmentsShortcut(
  {
    projectKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/segments', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withHash(hash),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toAIConfigsShortcut(
  {
    projectKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs', { projectKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withHash(hash),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

// Note: this navigator is temporary until we have useNavigator hook
export function toAIConfigShortcut(
  {
    projectKey,
    configKey,
    environmentKey,
    environmentKeys,
  }: { projectKey: string; configKey: string; environmentKey: string; environmentKeys: Set<string> },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey', { projectKey, configKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [...environmentKeys], search)),
    withHash(hash),
    withNewProjectContext({ selectedEnvironmentKey: environmentKey, environmentKeys, projectKey }),
  );
}

export function toAIConfigsWelcome({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/agentcontrol-welcome', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIConfigsWelcomeSetupSdk({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/agentcontrol-welcome/setup-sdk', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIConfigs({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIConfig(
  { projectKey, configKey }: { projectKey: string; configKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey', { projectKey, configKey }),
    withSearchParams(search),
    withHash(hash),
  );
}
export function toAIConfigVariations(
  { projectKey, configKey }: { projectKey: string; configKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/variations', { projectKey, configKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIConfigCompareView(
  { projectKey, configKey, variationKey }: { projectKey: string; configKey: string; variationKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/variations/:variationKey', {
      projectKey,
      configKey,
      variationKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIConfigTargeting(
  { projectKey, configKey, environmentKey }: { projectKey: string; configKey: string; environmentKey?: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/targeting', { projectKey, configKey }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}
export function toAIConfigMonitoring(
  { projectKey, configKey }: { projectKey: string; configKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/monitoring', { projectKey, configKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIConfigPlayground(
  { projectKey, configKey }: { projectKey: string; configKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/playground', { projectKey, configKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAiConfigPendingChanges(
  { projectKey, flagKey, environmentKey }: { projectKey: string; flagKey: string; environmentKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/:configKey/targeting/pending-changes', {
      projectKey,
      configKey: flagKey,
    }),
    withSearchParams(buildEnvironmentSearchParams(environmentKey, [], search)),
    withHash(hash),
    withProjectContext({ selectedEnvironmentKey: environmentKey }),
  );
}

export function toCreateAIConfig({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-configs/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIMonitoring({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-monitoring', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIMonitoringTrends({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-monitoring/trends', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibrary({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryModels({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/models', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryModel(
  { projectKey, configKey }: { projectKey: string; configKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/models/:configKey', { projectKey, configKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryTools({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/tools', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryTool(
  { projectKey, toolKey }: { projectKey: string; toolKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/tool/:toolKey', { projectKey, toolKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAIEvaluation({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/evaluation', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toPlaygrounds({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/playgrounds/active', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toPlaygroundsArchived({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/playgrounds/archived', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toPlaygroundDetails(
  { projectKey, evaluationId }: { projectKey: string; evaluationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/playgrounds/:evaluationId', { projectKey, evaluationId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toPlaygroundDetailsWithCompare(
  { projectKey, evaluationId }: { projectKey: string; evaluationId: string },
  { compareEvaluationIds, search, hash }: URLOptions & { compareEvaluationIds?: readonly string[] } = {},
) {
  return toPlaygroundDetails(
    { projectKey, evaluationId },
    {
      search: mergePlaygroundCompareIntoSearchParams(search, compareEvaluationIds ?? []),
      hash,
    },
  );
}

// Helper for the playground detail route: the URL segment is `:evaluationId` for historical reasons but is
// interpreted as a playground id by the loader; this builds hrefs without naming confusion at call sites.
export function toPlaygroundDetailsByPlaygroundId(
  { projectKey, playgroundId }: { projectKey: string; playgroundId: string },
  { search, hash }: URLOptions = {},
) {
  return toPlaygroundDetails({ projectKey, evaluationId: playgroundId }, { search, hash });
}

export {
  buildPlaygroundCompareSearchParams,
  mergePlaygroundCompareIntoSearchParams,
  orderedPlaygroundEvaluationIds,
  parsePlaygroundCompareEvaluationIds,
  PLAYGROUND_COMPARE_EVALUATION_QUERY_KEY,
} from './internal/playgroundCompareSearchParams';

export function toEvaluations({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/evaluations', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIEvaluationRuns({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/evaluations', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIEvaluationRunDetails(
  { projectKey, evaluationId, runId }: { projectKey: string; evaluationId: string; runId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/evaluations/:evaluationId/runs/:runId', {
      projectKey,
      evaluationId,
      runId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAILibrarySnippets({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/snippets', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibrarySnippetsNew({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/snippets/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibrarySnippet(
  { projectKey, snippetKey }: { projectKey: string; snippetKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/snippets/:snippetKey', { projectKey, snippetKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibrarySkills({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/skills', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibrarySkill(
  { projectKey, skillKey }: { projectKey: string; skillKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/skills/:skillKey', { projectKey, skillKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryDatasets({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/datasets', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryDataset(
  { projectKey, datasetKey }: { projectKey: string; datasetKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/datasets/:datasetKey', { projectKey, datasetKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryJudgeTemplates({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/judge-templates', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILibraryJudgeTemplate(
  { projectKey, templateKey }: { projectKey: string; templateKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai-library/judge-templates/:templateKey', { projectKey, templateKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAILanding({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/projects/:projectKey/ai', { projectKey }), withSearchParams(search), withHash(hash));
}

export function toCodingAgents({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/coding-agents', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIAgentGraphs({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/graphs', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIAgentGraph(
  { projectKey, graphKey }: { projectKey: string; graphKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/graphs/:graphKey', { projectKey, graphKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIOptimizeAgentList({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/optimize-agent', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIOptimizeAgentNew({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/optimize-agent/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIOptimizeAgentRuns(
  { projectKey, optimizationId }: { projectKey: string; optimizationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/optimize-agent/:optimizationId/runs', {
      projectKey,
      optimizationId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIOptimizeAgentConfiguration(
  { projectKey, optimizationId }: { projectKey: string; optimizationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/optimize-agent/:optimizationId/configuration', {
      projectKey,
      optimizationId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAIOptimizeAgentRun(
  { projectKey, optimizationId, runId }: { projectKey: string; optimizationId: string; runId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/ai/optimize-agent/:optimizationId/runs/:runId', {
      projectKey,
      optimizationId,
      runId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toInsights({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/insights'), withSearchParams(search), withHash(hash));
}

export function toGuardedRollouts({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/guarded-rollouts', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toFlagReleaseDashboard({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/releases', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toSessions({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/sessions', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toSession(
  { projectKey, sessionId }: { projectKey: string; sessionId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/sessions/:sessionId', { projectKey, sessionId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toErrors({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/errors', {
      projectKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toError(
  { projectKey, issueId }: { projectKey: string; issueId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/errors/:issueId', {
      projectKey,
      issueId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toErrorInstances(
  { projectKey, issueId }: { projectKey: string; issueId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/errors/:issueId/instances', { projectKey, issueId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toErrorInstance(
  { projectKey, issueId, instanceId }: { projectKey: string; issueId: string; instanceId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/errors/:issueId/instances/:instanceId', {
      projectKey,
      issueId,
      instanceId,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toLogs({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/projects/:projectKey/logs', { projectKey }), withSearchParams(search), withHash(hash));
}

export function toTraces({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/projects/:projectKey/traces', { projectKey }), withSearchParams(search), withHash(hash));
}

export function toDatabases({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/databases', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toAlerts({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/projects/:projectKey/alerts', { projectKey }), withSearchParams(search), withHash(hash));
}

export function toAlert(
  { projectKey, alertId }: { projectKey: string; alertId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/alerts/:alertId', { projectKey, alertId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toNewAlert({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/alerts/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toEditAlert(
  { projectKey, alertId }: { projectKey: string; alertId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/alerts/:alertId/edit', { projectKey, alertId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboards({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboard(
  { projectKey, dashboardId }: { projectKey: string; dashboardId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/:dashboardId', { projectKey, dashboardId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboardsNew({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboardsGraphNew(
  { projectKey, dashboardId }: { projectKey: string; dashboardId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/:dashboardId/new', { projectKey, dashboardId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboardGraph(
  { projectKey, dashboardId, graphId }: { projectKey: string; dashboardId: string; graphId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/:dashboardId/:graphId', { projectKey, dashboardId, graphId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toDashboardGraphEdit(
  { projectKey, dashboardId, graphId }: { projectKey: string; dashboardId: string; graphId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/dashboards/:dashboardId/:graphId/edit', { projectKey, dashboardId, graphId }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toObservabilitySettings({ projectKey }: { projectKey: string }) {
  return buildPath(appRoute('/projects/:projectKey/settings/observability', { projectKey }));
}

export function toObservabilityFilterRulesSettings({ projectKey, product }: { projectKey: string; product: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/observability/filter-rules/:product', { projectKey, product }),
  );
}

export function toProjectRoles({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/project'), withSearchParams(search), withHash(hash));
}

export function toOrganizationRoles({ search, hash }: URLOptions = {}) {
  return buildPath(appRoute('/settings/roles/organization'), withSearchParams(search), withHash(hash));
}

export function toViewProjectRole({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/roles/project/:key/view', { key: roleKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toViewOrganizationRole({ roleKey }: { roleKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/settings/roles/organization/:key/view', { key: roleKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

// Carefully selected array of routes to preserve when switching selected projects
//
// NOTE: Routes w/ segments should not be included here the projectKey is the
// only parameter passed to the next route.
const acceptedReplaceRoutes = [
  '/projects/:projectKey',
  '/projects/:projectKey/ai',
  '/projects/:projectKey/agentcontrol',
  '/projects/:projectKey/agentcontrol-welcome',
  '/projects/:projectKey/ai-configs',
  '/projects/:projectKey/ai-configs/new',
  '/projects/:projectKey/ai/evaluation',
  '/projects/:projectKey/ai/evaluations',
  '/projects/:projectKey/ai/playgrounds/active',
  '/projects/:projectKey/ai/playgrounds/archived',
  '/projects/:projectKey/ai/graphs',
  '/projects/:projectKey/ai/coding-agents',
  '/projects/:projectKey/ai/optimize-agent',
  '/projects/:projectKey/ai-library',
  '/projects/:projectKey/ai-library/datasets',
  '/projects/:projectKey/ai-library/judge-templates',
  '/projects/:projectKey/ai-library/models',
  '/projects/:projectKey/ai-library/skills',
  '/projects/:projectKey/ai-library/snippets',
  '/projects/:projectKey/ai-library/snippets/new',
  '/projects/:projectKey/ai-library/tools',
  '/projects/:projectKey/ai-monitoring',
  '/projects/:projectKey/ai-monitoring/trends',
  '/projects/:projectKey/alerts',
  '/projects/:projectKey/alerts/new',
  '/projects/:projectKey/approvals',
  '/projects/:projectKey/audit',
  '/projects/:projectKey/compare',
  '/projects/:projectKey/contexts',
  '/projects/:projectKey/contexts/kinds',
  '/projects/:projectKey/dashboards',
  '/projects/:projectKey/dashboards/databases',
  '/projects/:projectKey/dashboards/new',
  '/projects/:projectKey/errors',
  '/projects/:projectKey/experiments',
  '/projects/:projectKey/flags',
  '/projects/:projectKey/flags/connect',
  '/projects/:projectKey/flags/new',
  '/projects/:projectKey/guarded-rollouts',
  '/projects/:projectKey/holdouts',
  '/projects/:projectKey/holdouts/new',
  '/projects/:projectKey/home',
  '/projects/:projectKey/live',
  '/projects/:projectKey/logs',
  '/projects/:projectKey/data-sources',
  '/projects/:projectKey/data-sources/assignments',
  '/projects/:projectKey/data-sources/metrics',
  '/projects/:projectKey/metric-data-sources',
  '/projects/:projectKey/metrics',
  '/projects/:projectKey/metrics/new',
  '/projects/:projectKey/metrics/groups/new',
  '/projects/:projectKey/metrics/events',
  '/projects/:projectKey/multi-armed-bandits',
  '/projects/:projectKey/observe',
  '/projects/:projectKey/observe/new-metric',
  '/projects/:projectKey/sdk-setup',
  '/projects/:projectKey/segments',
  '/projects/:projectKey/segments/new',
  '/projects/:projectKey/sessions',
  '/projects/:projectKey/settings',
  '/projects/:projectKey/settings/approvals',
  '/projects/:projectKey/settings/automations',
  '/projects/:projectKey/settings/context-kinds',
  '/projects/:projectKey/settings/environments',
  '/projects/:projectKey/settings/environments/new',
  '/projects/:projectKey/settings/flags',
  '/projects/:projectKey/settings/lifecycle',
  '/projects/:projectKey/settings/observability',
  '/projects/:projectKey/settings/payload-filters',
  '/projects/:projectKey/settings/releases',
  '/projects/:projectKey/settings/vega',
  '/projects/:projectKey/settings/views',
  '/projects/:projectKey/settings/release-pipelines',
  '/projects/:projectKey/settings/release-pipelines/new',
  '/projects/:projectKey/traces',
  '/projects/:projectKey/product-analytics',
  '/projects/:projectKey/product-analytics/cohorts',
  '/projects/:projectKey/product-analytics/dashboards',
  '/projects/:projectKey/product-analytics/events',
  '/projects/:projectKey/product-analytics/user-activity',
  '/projects/:projectKey/product-analytics/user-attributes',
] as const satisfies Array<AppRoute & `/projects/:projectKey${string}`>;

type AcceptedReplaceRoute = (typeof acceptedReplaceRoutes)[number];

function replaceRoutePattern(route: AcceptedReplaceRoute): string {
  return route.endsWith('/*') ? route : `${route}/*`;
}

/** Rebuild pathname for the target project; preserves static sub-paths but drops entity keys. */
function pathForSwitchedProject(matchedRoute: AcceptedReplaceRoute, pathname: string, projectKey: string): string {
  const routeMatch = matchPath(replaceRoutePattern(matchedRoute), pathname);
  const splat = routeMatch?.params['*'];
  const basePath = appRoute(matchedRoute as '/projects/:projectKey', { projectKey });

  if (!splat) {
    return basePath;
  }

  // The splat captures path segments beyond the matched route.
  // Multi-segment splats (e.g. "my-exp/results") always contain entity keys
  // that won't exist in the target project — drop them.
  // Single-segment splats could be entity keys ("my-flag") or static paths
  // ("new"). Only the "new" create-page keyword is safe to preserve;
  // entity keys would cause "not found" in the target project.
  if (splat === 'new') {
    return `${basePath}/new`;
  }

  return basePath;
}

export function toSwitchedProject(
  { projectKey, pathname = '/' }: { projectKey: string; pathname?: string },
  { search, hash }: URLOptions = {},
) {
  // Find all matching routes, sort by longest first
  const matchingCurrentRoutes = acceptedReplaceRoutes
    .filter((path) => matchPath(replaceRoutePattern(path), pathname))
    .toSorted((a, b) => b.length - a.length);

  const matchedRoute = matchingCurrentRoutes.at(0) ?? acceptedReplaceRoutes[0];

  return buildPath(
    pathForSwitchedProject(matchedRoute, pathname, projectKey),
    withSearchParams(withoutProjectScopedSearchParams(search)),
    withHash(hash),
  );
}

export function toSwitchedProjectView(
  { projectKey, viewKey, pathname = '/' }: { projectKey: string; viewKey: string; pathname?: string },
  { search, hash }: URLOptions = {},
) {
  // Find all matching routes, sort by longest first
  const matchingCurrentRoutes = acceptedReplaceRoutes
    .filter((path) => matchPath(replaceRoutePattern(path), pathname))
    .toSorted((a, b) => b.length - a.length);

  const matchedRoute = matchingCurrentRoutes.at(0) ?? acceptedReplaceRoutes[0];

  return buildPath(
    pathForSwitchedProject(matchedRoute, pathname, projectKey),
    withSearchParams(withoutProjectScopedSearchParams(search)),
    withHash(hash),
    withProjectContext({ selectedViewKey: viewKey }),
  );
}

export function toApprovalSettings({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/approvals', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toViews({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toViewsSettings({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/settings', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toCreateView({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toViewDetail({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources', { projectKey, viewKey }));
}

export function toViewLinkedResources({
  projectKey,
  viewKey,
  resource,
}: {
  projectKey: string;
  viewKey: string;
  resource: string;
}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', {
      projectKey,
      viewKey,
      resource,
    }),
  );
}

export function toViewLinkedFlags({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', {
      projectKey,
      viewKey,
      resource: 'flags',
    }),
  );
}

export function toViewLinkedSegments({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', {
      projectKey,
      viewKey,
      resource: 'segments',
    }),
  );
}

export function toViewLinkedMetrics({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', {
      projectKey,
      viewKey,
      resource: 'metrics',
    }),
  );
}

export function toViewLinkedAIConfigs({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource', {
      projectKey,
      viewKey,
      resource: 'ai-configs',
    }),
  );
}

export function toViewAddLinkedResources({
  projectKey,
  viewKey,
  resource,
}: {
  projectKey: string;
  viewKey: string;
  resource: string;
}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource/add', {
      projectKey,
      viewKey,
      resource,
    }),
  );
}

export function toViewLinkedFlagsApplicationEvaluated({
  projectKey,
  viewKey,
}: {
  projectKey: string;
  viewKey: string;
}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/linked-resources/:resource/application/evaluated', {
      projectKey,
      viewKey,
      resource: 'flags',
    }),
  );
}

export function toViewAddLinkedFlags({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return toViewAddLinkedResources({ projectKey, viewKey, resource: 'flags' });
}

export function toViewAddLinkedSegments({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return toViewAddLinkedResources({ projectKey, viewKey, resource: 'segments' });
}

export function toViewAddLinkedAIConfigs({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return toViewAddLinkedResources({ projectKey, viewKey, resource: 'ai-configs' });
}

export function toViewAddLinkedMetrics({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return toViewAddLinkedResources({ projectKey, viewKey, resource: 'metrics' });
}

export function toViewApplication({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(appRoute('/projects/:projectKey/settings/views/:viewKey/application', { projectKey, viewKey }));
}

export function toViewSettings({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(appRoute('/projects/:projectKey/settings/views/:viewKey/settings', { projectKey, viewKey }));
}

export function toViewSettingsApplicationEvaluated({ projectKey, viewKey }: { projectKey: string; viewKey: string }) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/settings/application/evaluated', {
      projectKey,
      viewKey,
    }),
  );
}

export function toViewSettingsApplicationUnevaluated(
  { projectKey, viewKey }: { projectKey: string; viewKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/views/:viewKey/settings/application/unevaluated', {
      projectKey,
      viewKey,
    }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toFlagLifecycleSettings({ projectKey }: { projectKey: string }) {
  return buildPath(appRoute('/projects/:projectKey/settings/lifecycle', { projectKey }));
}

export function toProjectAutomationsSettings(
  { projectKey }: { projectKey: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/automations', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toProjectVegaSettings({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/settings/vega', { projectKey }),
    withSearchParams(search),
    withHash(hash),
  );
}

export function toVega({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaHistory({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/history', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaWorkflows({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/workflows', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaNewWorkflow({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/workflows/new', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaWorkflow(
  { projectKey, workflowId }: { projectKey: string; workflowId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/workflows/:workflowId', { projectKey, workflowId }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaSettings({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/settings', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaArtifacts({ projectKey }: { projectKey: string }, { search, hash }: URLOptions = {}) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/artifacts', { projectKey }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaArtifact(
  { projectKey, type, externalId }: { projectKey: string; type: string; externalId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/artifacts/:type/:externalId', {
      projectKey,
      type,
      externalId,
    }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toVegaConversation(
  { projectKey, conversationId }: { projectKey: string; conversationId: string },
  { search, hash }: URLOptions = {},
) {
  return buildPath(
    appRoute('/projects/:projectKey/vega/:conversationId', { projectKey, conversationId }),
    withSearchParams(search),
    withHash(hash),
    withProjectContext(),
  );
}

export function toAuthorizationTokens() {
  return buildPath(appRoute('/settings/authorization/tokens'));
}

export function toAuthorizationOauthApps() {
  return buildPath(appRoute('/settings/authorization/oauth'));
}
